import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { LogIn, UserPlus, Mail, Lock, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { trackRedditEvent } from "@/lib/reddit-pixel";
import { trackAccountSignupCompleted, trackAdsSignupConversion } from "@/lib/analytics";

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get returnTo parameter from URL (default to /dashboard)
  const returnToParam = searchParams.get('returnTo') || '/dashboard';

  // Security: Only allow internal redirects (prevent open redirect vulnerability)
  const isValidReturnPath = (path: string) => {
    // Must start with / and not contain // or ://
    return path.startsWith('/') && !path.startsWith('//') && !path.includes('://');
  };

  const returnTo = isValidReturnPath(returnToParam) ? returnToParam : '/dashboard';

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleGoogleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}${returnTo}${returnTo === '/affiliate' ? '?fromAuth=true' : ''}`
        }
      });
      
      if (error) throw error;
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Add fromAuth parameter if returning to affiliate page
      const redirectUrl = returnTo === '/affiliate' ? `${returnTo}?fromAuth=true` : returnTo;
      navigate(redirectUrl);
      toast({
        title: "Welcome back!",
        description: "You have successfully logged in.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) throw error;

      // Note: affiliate conversion tracking is fired from AuthProvider on the
      // first verified sign-in, not here. Tracking pre-verification would
      // credit affiliates for unverified / bogus signups.

      // Track signup with Reddit Pixel + CAPI
      trackRedditEvent('SignUp', { email });

      // GA4: Track email signup
      trackAccountSignupCompleted('email');

      // Google Ads signup conversion — fire at submit so email signups are
      // counted immediately, not gated behind email verification.
      trackAdsSignupConversion(email);

      // Show beautiful email verification screen
      setSignupEmail(email);
      setShowEmailVerification(true);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      setForgotSent(true);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setForgotLoading(false);
    }
  };

  // If showing forgot password screen
  if (showForgotPassword) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center py-16 px-4">
          <div className="container max-w-md">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <img src="/wtc-logo.png" alt="WillTheyConvert" className="h-16 mx-auto mb-8" />

              <Card className="border-0 bg-white/80 backdrop-blur-sm rounded-3xl">
                <CardContent className="p-6">
                  {forgotSent ? (
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Mail className="w-6 h-6 text-green-600" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">Check your email</h2>
                      <p className="text-sm text-gray-600 mb-6">
                        We've sent a password reset link to <span className="font-semibold text-indigo-600">{forgotEmail}</span>
                      </p>
                      <button
                        onClick={() => {
                          setShowForgotPassword(false);
                          setForgotSent(false);
                          setForgotEmail("");
                        }}
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                      >
                        Back to login
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="text-center mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-1">Reset your password</h2>
                        <p className="text-sm text-gray-600">
                          Enter your email and we'll send you a reset link
                        </p>
                      </div>
                      <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="forgot-email" className="text-sm font-medium text-gray-700">
                            Email
                          </Label>
                          <Input
                            id="forgot-email"
                            type="email"
                            placeholder="you@example.com"
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            required
                            className="rounded-2xl h-12 border border-indigo-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                        <Button
                          type="submit"
                          className="w-full rounded-2xl h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium transition-all"
                          disabled={forgotLoading}
                        >
                          {forgotLoading ? "Sending..." : "Send Reset Link"}
                        </Button>
                      </form>
                      <div className="text-center mt-4">
                        <button
                          onClick={() => setShowForgotPassword(false)}
                          className="text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
                        >
                          Back to login
                        </button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // If showing email verification screen
  if (showEmailVerification) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center py-16 px-4">
          <div className="container max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="text-center"
            >
              <img src="/wtc-logo.png" alt="WillTheyConvert" className="h-16 mx-auto mb-8" />

              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-3">
                Check Your Email
              </h1>

              <p className="text-base text-gray-600 mb-8 max-w-md mx-auto">
                We've sent a verification link to <span className="font-semibold text-indigo-600">{signupEmail}</span>
              </p>

              <Card className="border-0 bg-white/80 backdrop-blur-sm rounded-3xl mb-6">
                <CardContent className="p-6">
                  <div className="space-y-4 text-left">
                    <div className="flex gap-3 items-start">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold mt-0.5">
                        1
                      </div>
                      <div>
                        <p className="text-sm text-gray-700">Open your inbox and find the email from WillTheyConvert</p>
                      </div>
                    </div>

                    <div className="flex gap-3 items-start">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold mt-0.5">
                        2
                      </div>
                      <div>
                        <p className="text-sm text-gray-700">Click the verification link to activate your account</p>
                      </div>
                    </div>

                    <div className="flex gap-3 items-start">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold mt-0.5">
                        3
                      </div>
                      <div>
                        <p className="text-sm text-gray-700">Start creating your first validation flow</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      <span className="font-medium text-gray-700">Didn't receive it?</span> Check your spam folder or wait a few minutes.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="rounded-2xl h-12 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium transition-all"
                >
                  Go to Dashboard
                </Button>
                <button
                  onClick={() => {
                    setShowEmailVerification(false);
                    setEmail("");
                    setPassword("");
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
                >
                  Back to login
                </button>
              </div>
            </motion.div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-12 px-4">
        <div className="container max-w-lg">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <img src="/wtc-logo.png" alt="WillTheyConvert" className="h-20 mx-auto mb-4" />
            <p className="text-base text-gray-600">
              Validate your product ideas before building them
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 rounded-full p-1.5 bg-white border border-indigo-100 mb-6 h-12">
                <TabsTrigger
                  value="login"
                  className="rounded-full h-full px-6 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-600 data-[state=active]:to-purple-600 data-[state=active]:text-white font-medium transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Login
                </TabsTrigger>
                <TabsTrigger
                  value="signup"
                  className="rounded-full h-full px-6 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-600 data-[state=active]:to-purple-600 data-[state=active]:text-white font-medium transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Sign Up
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="mt-0">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="border-0 bg-white/80 backdrop-blur-sm overflow-hidden rounded-3xl">
                  <form onSubmit={handleLogin}>
                    <CardContent className="space-y-4 pt-6 pb-6">
                      <div className="text-center mb-4">
                        <h2 className="text-2xl font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-1">Welcome Back</h2>
                        <p className="text-sm text-gray-600">
                          Log in to your account to manage your test flows
                        </p>
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full rounded-2xl h-12 border border-indigo-200 hover:border-indigo-300 hover:bg-indigo-50 font-medium transition-all"
                        onClick={handleGoogleLogin}
                      >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </Button>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="bg-white px-3 text-gray-500">
                            Or continue with email
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="login-email" className="text-sm font-medium text-gray-700">
                          Email
                        </Label>
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="rounded-2xl h-12 border border-indigo-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="login-password" className="text-sm font-medium text-gray-700">
                            Password
                          </Label>
                          <button
                            type="button"
                            onClick={() => {
                              setForgotEmail(email);
                              setShowForgotPassword(true);
                            }}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                          >
                            Forgot password?
                          </button>
                        </div>
                        <Input
                          id="login-password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="rounded-2xl h-12 border border-indigo-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full rounded-2xl h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium transition-all mt-2"
                        disabled={loading}
                      >
                        {loading ? "Logging in..." : "Log In"}
                      </Button>
                    </CardContent>
                  </form>
                </Card>
                </motion.div>
              </TabsContent>

              <TabsContent value="signup" className="mt-0">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="border-0 bg-white/80 backdrop-blur-sm overflow-hidden rounded-3xl">
                  <form onSubmit={handleSignUp}>
                    <CardContent className="space-y-4 pt-6 pb-6">
                      <div className="text-center mb-4">
                        <h2 className="text-2xl font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-1">Create an Account</h2>
                        <p className="text-sm text-gray-600">
                          Get started with WillTheyConvert to validate your product ideas
                        </p>
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full rounded-2xl h-12 border border-indigo-200 hover:border-indigo-300 hover:bg-indigo-50 font-medium transition-all"
                        onClick={handleGoogleLogin}
                      >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </Button>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="bg-white px-3 text-gray-500">
                            Or continue with email
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-name" className="text-sm font-medium text-gray-700">
                          Name
                        </Label>
                        <Input
                          id="signup-name"
                          type="text"
                          placeholder="John Doe"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          required
                          className="rounded-2xl h-12 border border-indigo-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-email" className="text-sm font-medium text-gray-700">
                          Email
                        </Label>
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="rounded-2xl h-12 border border-indigo-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-password" className="text-sm font-medium text-gray-700">
                          Password
                        </Label>
                        <Input
                          id="signup-password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="rounded-2xl h-12 border border-indigo-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full rounded-2xl h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium transition-all mt-2"
                        disabled={loading}
                      >
                        {loading ? "Creating account..." : "Create Account"}
                      </Button>
                    </CardContent>
                  </form>
                </Card>
                </motion.div>
              </TabsContent>
            </Tabs>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-8 text-center"
            >
              <p className="text-sm text-gray-500 mb-4">Trusted by product builders worldwide</p>
              <div className="flex justify-center gap-6 text-xs text-gray-400">
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span>Secure & encrypted</span>
                </div>
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>Privacy protected</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Auth;
